import React, { useEffect, useState } from "react";
import { Col, Image, Toast } from "react-bootstrap";
import Draggable from "react-draggable";
import { Trans, useTranslation } from "react-i18next";
import { NumericFormat as NumberFormat } from "react-number-format";
import { useDispatch, useSelector } from "react-redux";
import AnimatedCol from "../../../components/AnimatedCol";
import ContentExpansion from "../../../components/ContentExpansion";
import ConvertToString, { getClassName, isValidNumber } from "../../../components/Converter";
import PlusIcon from "../../../components/Icons/PlusIcon";
import KiloNewton from "../../../components/Math/Units/Metric/KiloNewton";
import Metre from "../../../components/Math/Units/Metric/Metre";
import AlertBox from "../../../components/Messages";
import { NumberFormatText } from "../../../components/NumberFormatText";
import { RowBody } from "../../../components/RowValue/RowBody";
import { ResultSpan } from "../../../components/styledComponents/Result";
import HiddenWrapperRow from "../../../components/UI/HiddenWrapperRow";
import { decimalSeparator, thousandSeparator } from "../../../i18n";
import druckImageDE from "../../../images/druck_1.png";
import druckImageEN from "../../../images/20231011_Druckpfahlgruppe_EN.png";
import { MaxIntToINF } from "../../../services/imperialMetricKonverter.service";
import { berechneSchichtenGruppe } from "../../../services/Schichten/berechneSchichtenGruppe";
import {
  selectIsLreqFactorValid,
  selectLa,
  selectLaengeGruppeErfordelich,
  selectLb,
  selectModelFaktor,
  selectPerimeterPileGroup,
  selectPfahlneigungZurVertikalen,
  selectSchichteninput,
  selectShowError_Druckpfahlgruppe,
  selectTeilsicherheitsbeiwert,
  setLa,
  setLb,
  setPerimeterPileGroup,
} from "../../../store/slices/aeussereSlice";
import { updateDGruppeValue } from "../../../store/slices/aeussereSlice.js";
import {
  selectBemessungslastGruppeGesamt,
  setBemessungslastGruppeGesamt,
} from "../../../store/slices/bemessungslastGruppeGesamtSlice";
import { selectBemessungslast } from "../../../store/slices/bemessungslastSlice";
import { selectBohrkronendurchmesser } from "../../../store/slices/bohrkronendurchmesserSlice";
import {
  selectFrontendZustand,
  setKompressionspfahlgruppeDetails,
} from "../../../store/slices/frontendZustandSlice.js";
import DruckpfahlgruppeTable from "./DruckpfahlgruppeTable";

export default function Druckpfahlgruppe() {
  const { i18n, t } = useTranslation("translation");
  const dispatch = useDispatch();

  const schichtenInput = useSelector(selectSchichteninput);
  const bemessungslast = useSelector(selectBemessungslast);
  const perimeterPileGroup = useSelector(selectPerimeterPileGroup);
  const bemessungslastGruppeGesamt = useSelector(selectBemessungslastGruppeGesamt);
  const { kompressionspfahlgruppenDetails } = useSelector(selectFrontendZustand);
  const laengeGruppeErfordelich = useSelector(selectLaengeGruppeErfordelich);
  const bohrkronendurchmesser = useSelector(selectBohrkronendurchmesser);
  const pfahlneigungZurVertikalen = useSelector(selectPfahlneigungZurVertikalen);
  const la = useSelector(selectLa);
  const lb = useSelector(selectLb);
  const teilsicherheitsbeiwert = useSelector(selectTeilsicherheitsbeiwert);
  const modellfaktor = useSelector(selectModelFaktor);

  const isLreqValid = useSelector(selectIsLreqFactorValid);
  const showError = useSelector(selectShowError_Druckpfahlgruppe);

  const [showDruckImage, setShowDruckImage] = useState(false);
  useEffect(() => {
    if (!isValidNumber(la) || !isValidNumber(lb)) {
      dispatch(setPerimeterPileGroup(Number.NaN));
      return;
    }
    dispatch(setPerimeterPileGroup(2 * (Number(la) + Number(lb))));
  }, [la, lb]);

  useEffect(() => {
    dispatch(setBemessungslastGruppeGesamt(Number(bemessungslastGruppeGesamt)));
  }, [bemessungslast]);

  useEffect(() => {
    const result = berechneSchichtenGruppe({
      schichtenInput: schichtenInput,
      bohrkronendurchmesser: Number(bohrkronendurchmesser),
      teilsicherheitsbeiwert: teilsicherheitsbeiwert,
      modellfaktor: modellfaktor,
      pfahlneigungZurVertikalen: Number(pfahlneigungZurVertikalen),
      bemessungslastGruppeGesamt: Number(bemessungslastGruppeGesamt),
      umfangErsatzpfahl: perimeterPileGroup,
    });
    dispatch(updateDGruppeValue({ schichtenCalculated: result.schichten }));
    dispatch(updateDGruppeValue({ lreq: result.resultSumLi }));
    dispatch(updateDGruppeValue({ sumRsdi: result.resultSumRsdi }));
    dispatch(
      updateDGruppeValue({
        bemessungspfahlwiderstand: result.bemessungspfahlwiderstand,
      })
    );
  }, [
    schichtenInput,
    bemessungslastGruppeGesamt,
    bohrkronendurchmesser,
    modellfaktor,
    teilsicherheitsbeiwert,
    perimeterPileGroup,
    pfahlneigungZurVertikalen,
  ]);

  return (
    <>
      <div className="row">
        <ContentExpansion
          expand={kompressionspfahlgruppenDetails}
          reducer={setKompressionspfahlgruppeDetails}
        />
      </div>

      <div className="row">
        <div className="col-md-12">
          <h3>{t("aeussre.dgruppeHeadline")}</h3>
        </div>
      </div>

      <AnimatedCol>
        <RowBody
          titleValue={<p>{t("aeussre.designLoadPileGroup")}</p>}
          indicesValue={
            <span>
              F<sub>d,tot</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(bemessungslastGruppeGesamt)}
              value={bemessungslastGruppeGesamt}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setBemessungslastGruppeGesamt(value.floatValue))}
              allowNegative={true}
            />
          }
          unitValue={<KiloNewton />}
          formulaValue={
            <span onClick={() => setShowDruckImage(!showDruckImage)}>
              <PlusIcon />
            </span>
          }
        />
      </AnimatedCol>

      <HiddenWrapperRow>
        <ShowImageDetails
          show={showDruckImage}
          setShow={setShowDruckImage}
          image={i18n.language === "de-DE" ? druckImageDE : druckImageEN}
        />
      </HiddenWrapperRow>

      <AnimatedCol>
        <RowBody
          titleValue={<p>{t("aeussre.partialSafetyFactor")}</p>}
          indicesValue={
            <span>
              &gamma;<sub>s</sub>
            </span>
          }
          inputValue={
            <span className="input">
              <NumberFormatText value={teilsicherheitsbeiwert} />
            </span>
          }
        />
      </AnimatedCol>
      <AnimatedCol>
        <RowBody
          titleValue={<p>{t("aeussre.overallDimensions")}</p>}
          indicesValue={
            <span>
              l<sub>a</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(la)}
              value={la}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setLa(value.floatValue))}
              allowNegative={true}
              decimalScale={2}
              //fixedDecimalScale={true}
            />
          }
          unitValue={<Metre />}
        />
      </AnimatedCol>

      <AnimatedCol>
        <RowBody
          titleValue={<p>{t("aeussre.overallDimensions_B")}</p>}
          indicesValue={
            <span>
              l<sub>b</sub>
            </span>
          }
          inputValue={
            <NumberFormat
              className={getClassName(lb)}
              value={lb}
              decimalSeparator={decimalSeparator()}
              thousandSeparator={thousandSeparator()}
              displayType="input"
              type="text"
              onValueChange={(value) => dispatch(setLb(value.floatValue))}
              allowNegative={true}
              decimalScale={2}
              //fixedDecimalScale={true}
            />
          }
          unitValue={<Metre />}
        />
      </AnimatedCol>

      <AnimatedCol>
        <RowBody
          titleValue={<p>{t("aeussre.perimeterPileGroup")}</p>}
          indicesValue={<span>U</span>}
          inputValue={
            <span className="input">
              <NumberFormatText value={perimeterPileGroup} decimalScale={2} />
            </span>
          }
          unitValue={<Metre />}
        />
      </AnimatedCol>

      {kompressionspfahlgruppenDetails === true ? (
        <>
          <br />
          <AnimatedCol>
            <DruckpfahlgruppeTable />
          </AnimatedCol>
        </>
      ) : null}
      <AnimatedCol>
        <RowBody
          titleValue={<p>{t("aeussre.requiredPileLength")}</p>}
          indicesValue={
            <span>
              L<sub>req</sub>
            </span>
          }
          inputValue={
            <ResultSpan valid={isLreqValid}>
              {ConvertToString(MaxIntToINF(laengeGruppeErfordelich, 2), 2)}
            </ResultSpan>
          }
          unitValue={<Metre />}
        />
      </AnimatedCol>
      <Col md={8}>
        <AlertBox
          show={showError}
          message={
            <Trans
              i18nKey="aeussre.selectLongerPileLnom"
              values={{
                lnom: "L<sub>nom</sub>",
                ge: "≥",
                lreq: "L<sub>req</sub>",
              }}
              components={{ sub: <sub /> }}
            />
          }
        />
      </Col>
    </>
  );
}

function ShowImageDetails({ show, setShow, image }) {
  const nodeRef = React.useRef(null);
  return (
    <Draggable nodeRef={nodeRef}>
      <Toast
        ref={nodeRef}
        onClose={() => setShow(!show)}
        show={show}
        animation={false}
        style={{
          zIndex: 1000,
          backgroundColor: "white",
          position: "absolute",
          width: "auto",
          height: "auto",
        }}
      >
        <Toast.Header className="d-flex flex-row-reverse"></Toast.Header>
        <Toast.Body>
          <Image src={image} width={"300px"} alt="" />
        </Toast.Body>
      </Toast>
    </Draggable>
  );
}
